import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { Task, TaskStatus } from '../../../api/types';
import { useAuth } from '../../../app/providers/useAuth';
import { changeTaskStatus } from '../api/tasksApi';

interface ChangeTaskStatusParams {
  taskId: number;
  status: TaskStatus;
}

// §8.1: инвалидирует ['tasks', userId] (+ subtasks родителя, если это подзадача).
// PARA-135 (АР-9, AC-14, AC-E1, AC-E2) — оптимистичное обновление: onMutate
// пишет новый статус в кэш до ответа сервера, onError восстанавливает снимок,
// а инвалидация переехала из onSuccess в onSettled — иначе после ошибки кэш
// остался бы расходиться с сервером.
export function useChangeTaskStatus() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const tasksKey = ['tasks', user?.id];

  return useMutation({
    mutationFn: ({ taskId, status }: ChangeTaskStatusParams) => changeTaskStatus(taskId, status),
    onMutate: async ({ taskId, status }: ChangeTaskStatusParams) => {
      // Иначе уже летящий рефетч перезапишет оптимистичное значение старым.
      await queryClient.cancelQueries({ queryKey: tasksKey });
      const previous = queryClient.getQueryData<Task[]>(tasksKey);
      queryClient.setQueryData<Task[]>(tasksKey, (old) =>
        old?.map((task) => (task.id === taskId ? { ...task, status } : task)),
      );
      // Снимок может быть undefined (кэш пуст) — тогда и писать было нечего.
      return { previous };
    },
    onError: (_error, _variables, context) => {
      queryClient.setQueryData(tasksKey, context?.previous);
    },
    onSettled: (task) => {
      void queryClient.invalidateQueries({ queryKey: tasksKey });
      if (task !== undefined && task.parent_task_id !== null) {
        void queryClient.invalidateQueries({
          queryKey: ['tasks', user?.id, task.parent_task_id, 'subtasks'],
        });
      }
    },
  });
}
